import {
    Box, Typography, LinearProgress, Chip, Paper, Grid, Modal
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import DoneIcon from "@mui/icons-material/Done";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import * as React from "react";

// ---------- Tipos ----------
export type ProcessStep = {
    /** Título del paso */
    label: string;
    /** Subtítulo/chiquitexto del paso */
    sub?: string;
    /** Lista de tareas/etiquetas (se pintan como chips) */
    tasks?: string[];
    /** Icono cuando el paso está "pendiente" (no actual ni completado) */
    icon?: React.ReactNode;
};

export type StatusChipLabels = {
    done?: string;      // default: "Completado"
    doing?: string;     // default: "En proceso"
    pending?: string;   // default: "Pendiente"
};

type ProcessPanelProps = {
    /** Título grande arriba */
    title?: string;                       // default: "Generando Carta porte"
    /** Subtítulo bajo el título */
    subtitle?: string;                    // default: "Estamos trabajando en tu solicitud"
    /** Progreso 0..100 para calcular el paso automáticamente */
    progress?: number;                    // default: 0
    /** Si prefieres controlar tú el paso actual, pásalo aquí (0-based). Tiene prioridad sobre `progress`. */
    currentStep?: number;
    /** Pasos a renderizar */
    steps?: ProcessStep[];                // default: ver DEFAULT_STEPS
    /** Textos de chips de estado */
    labels?: StatusChipLabels;
    /** Nodo opcional para reemplazar el panel derecho completo */
    rightPanel?: React.ReactNode;
    /** Mostrar como modal superpuesto */
    asOverlay?: boolean;
    open?: boolean;
    onClose?: () => void;
};

// ---------- Defaults ----------
const DEFAULT_STEPS: ProcessStep[] = [
    { label: "Preparando solicitud", sub: "Recopilando datos", tasks: ["Validar campos", "Verificar formato", "Comprobar duplicados"] },
    { label: "Gestionando proceso", sub: "Actualizando base de datos", tasks: ["Notificando al SAT"] },
    { label: "Finalizando solicitud", sub: "Completando la solicitud", tasks: ["Generando PDF", "Enviando Notificaciones", "Terminando Proceso"] },
];

const DEFAULT_LABELS: Required<StatusChipLabels> = {
    done: "Completado",
    doing: "En proceso",
    pending: "Pendiente",
};

// ---------- Componente ----------
const ProcessPanel: React.FC<ProcessPanelProps> = ({
    title = "Generando Carta porte",
    subtitle = "Estamos trabajando en tu solicitud",
    progress = 0,
    currentStep,
    steps = DEFAULT_STEPS,
    labels = DEFAULT_LABELS,
    rightPanel,
    asOverlay = false,
    open = false,
    onClose,
}) => {
    const total = steps.length || 1;

    // Si currentStep viene en props lo usamos; si no, lo derivamos del progress.
    const derivedStep =
        typeof currentStep === "number"
            ? Math.max(0, Math.min(total - 1, currentStep))
            : Math.min(total - 1, Math.floor((progress / 100) * total));

    const getStatusChip = (index: number) => {
        if (index < derivedStep) {
            return (
                <Chip
                    label={labels.done ?? DEFAULT_LABELS.done}
                    size="small"
                    sx={{ backgroundColor: "#34D399", color: "#fff", fontWeight: 600 }}
                />
            );
        }
        if (index === derivedStep) {
            return (
                <Chip
                    label={labels.doing ?? DEFAULT_LABELS.doing}
                    size="small"
                    sx={{ backgroundColor: "#DBEAFE", color: "#2563EB", fontWeight: 600 }}
                />
            );
        }
        return (
            <Chip
                label={labels.pending ?? DEFAULT_LABELS.pending}
                size="small"
                sx={{ backgroundColor: "#E5E7EB", color: "#6B7280", fontWeight: 600 }}
            />
        );
    };

    const content = (
        <Box sx={{ p: 4, backgroundColor: "rgba(249, 248, 248, 1)", borderRadius: 3 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <AccessTimeIcon sx={{ fontSize: 32, color: "#3B82F6" }} />
                <Box>
                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                </Box>
            </Box>

            {/* Progreso */}
            <Box mb={4}>
                <Typography fontWeight={600} fontSize={14} mb={1}>Progreso General</Typography>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 8,
                        backgroundColor: "#E5E7EB",
                        "& .MuiLinearProgress-bar": { backgroundColor: "#3B82F6" },
                        borderRadius: 4,
                    }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography fontSize={12} color="text.secondary">Iniciado</Typography>
                    <Typography fontSize={12} color="text.secondary">Paso {derivedStep + 1} de {total}</Typography>
                    <Typography fontSize={12} fontWeight={600} color="#2563EB">{Math.max(0, Math.min(100, Math.round(progress)))}% completado</Typography>
                </Box>
            </Box>

            {/* Contenido */}
            <Grid container spacing={3}>
                {/* Izquierda: Pasos */}
                <Grid item xs={12} md={8}>
                    <Typography fontWeight={600} fontSize={14} mb={2}>Estado del Proceso</Typography>

                    {steps.map((s, i) => {
                        const isCurrent = i === derivedStep;
                        const isCompleted = i < derivedStep;
                        return (
                            <Paper
                                key={i}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderColor: isCurrent ? "#BFDBFE" : isCompleted ? "transparent" : "#E5E7EB",
                                    backgroundColor: isCurrent ? "#EFF6FF" : isCompleted ? "rgb(203, 227, 217)" : "#F9FAFB",
                                    borderRadius: 3,
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Box
                                        sx={{
                                            width: 32, height: 32, borderRadius: "50%",
                                            backgroundColor: isCurrent ? "#DBEAFE" : "#E5E7EB",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: isCurrent ? "#2563EB" : "#6B7280",
                                        }}
                                    >
                                        {isCompleted ? (
                                            <DoneIcon fontSize="small" sx={{ color: "#10B981" }} />
                                        ) : isCurrent ? (
                                            <CircularProgress size={16} thickness={5} sx={{ color: "#2563EB" }} />
                                        ) : (
                                            s.icon ?? null
                                        )}
                                    </Box>

                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography fontWeight={600} color={isCurrent ? "#2563EB" : "text.secondary"}>
                                                {s.label}
                                            </Typography>
                                            {getStatusChip(i)}
                                        </Box>
                                        {s.sub && (
                                            <Typography variant="body2" color="text.secondary">{s.sub}</Typography>
                                        )}
                                    </Box>
                                </Box>

                                {!!s.tasks?.length && (
                                    <Box mt={1} display="flex" flexWrap="wrap" gap={1} ml={6}>
                                        {s.tasks!.map((t, idx) => (
                                            <Chip
                                                key={idx}
                                                label={`• ${t}`}
                                                size="small"
                                                sx={{ backgroundColor: "#E5E7EB", color: "#4B5563", fontWeight: 500, fontSize: 12 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}
                </Grid>

                {/* Derecha: Panel lateral (parametrizable) */}
                <Grid item xs={12} md={4}>
                    {rightPanel ?? (
                        <Box
                            sx={{
                                height: "100%",
                                border: "1px solid #BFDBFE",
                                borderRadius: 3,
                                backgroundColor: "#EFF6FF",
                                p: 3,
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <AccessTimeIcon fontSize="small" sx={{ color: "#2563EB" }} />
                                <Typography fontWeight={600} color="#2563EB">Procesando solicitud</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={2} sx={{ textAlign: "justify" }}>
                                Mantén esta ventana abierta mientras completamos tu solicitud.
                            </Typography>

                            <Box sx={{ border: "1px solid #FCD34D", backgroundColor: "#FFFBEB", borderRadius: 2, p: 2, mb: 2 }}>
                                <Typography fontWeight={600} color="#D97706" mb={0.5}>⚠️ Seguridad</Typography>
                                <Typography variant="body2" color="#92400E" sx={{ textAlign: "justify" }}>
                                    Estamos validando tus datos para asegurar que toda la información esté correcta.
                                </Typography>
                            </Box>

                            <Box sx={{ border: "1px solid rgb(191, 227, 212)", backgroundColor: "#ECFDF5", borderRadius: 2, p: 2 }}>
                                <Typography fontWeight={600} color="#059669" mb={0.5}>⚡ Optimización</Typography>
                                <Typography variant="body2" color="#065F46" sx={{ textAlign: "justify" }}>
                                    Estamos optimizando tu solicitud para un procesamiento más rápido.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );

    if (asOverlay) {
        return (
            <Modal
                open={open}
                onClose={onClose}
                aria-labelledby="processing-overlay-title"
                aria-describedby="processing-overlay-description"
                BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)" } }}
                disableScrollLock
            >
                <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 2 }}>
                    <Box sx={{ width: { xs: "100%", sm: "90%", md: 900 }, maxHeight: "90vh", overflow: "auto", borderRadius: 3, boxShadow: 24, bgcolor: "background.paper" }}>
                        {content}
                    </Box>
                </Box>
            </Modal>
        );
    }

    return content;
};

export default ProcessPanel;
